/**
 * File Service
 * 파일 업로드 관련 API (Pre-signed URL, S3 직접 업로드)
 */

import { apiClient, ApiError } from './apiClient';

/** Swagger: PresignedUploadUrlResponseDto - preSignedUrl, fileUrl */
export interface PresignedUploadResponseDto {
	preSignedUrl: string;
	fileUrl: string;
	[key: string]: unknown;
}

/**
 * Pre-signed URL 발급
 * Swagger: POST /file/presigned-url
 * - fileType, fileName, uploadPath(required, enum: CREATOR | USER | EDU)
 */
export const getPresignedUploadUrl = async (params: {
	fileType: string;
	fileName: string;
	uploadPath?: 'CREATOR' | 'USER' | 'EDU';
}): Promise<PresignedUploadResponseDto> => {
	try {
		const body = {
			fileType: params.fileType,
			fileName: params.fileName,
			uploadPath: params.uploadPath ?? 'CREATOR',
		};
		const response = await apiClient.post<PresignedUploadResponseDto>('/file/presigned-url', body);
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || 'Pre-signed URL 발급에 실패했습니다.');
		}
		throw error;
	}
};

/**
 * S3에 파일 직접 업로드 (Pre-signed URL 사용)
 * 백엔드가 반환한 presigned URL을 수정하지 않고 그대로 사용합니다.
 */
export const uploadToS3 = async (presignedUrl: string, file: File, contentType?: string): Promise<void> => {
	const headers: Record<string, string> = {};
	if (contentType) headers['Content-Type'] = contentType;

	const response = await fetch(presignedUrl, {
		method: 'PUT',
		body: file,
		headers: Object.keys(headers).length > 0 ? headers : undefined,
	});

	if (!response.ok) {
		const body = await response.text().catch(() => '');
		throw new Error(`S3 업로드 실패: ${response.status} ${response.statusText}\n${body}`);
	}
};

const TEMP_UPLOAD_URL = 'https://i.namu.wiki/i/1WKJVdliAkPdhI7i3umKo_7-7i2EKIazzB5wiwecX_EiH_ZjWoZB5DA2mcpyxSLYkBC7DWorw8DEtb6lB-8kuSLrfRqbKyaqsuZbddwp74Z2aZwdLrX--AiQgtCPt2NnTXJ7JhKGnh0fguph27M4wA.webp';

/**
 * Pre-signed URL 발급 후 S3 업로드까지 한 번에 수행
 * 업로드 실패 시 TEMP_UPLOAD_URL 로 폴백
 * @returns 업로드된 파일의 최종 URL (fileUrl)
 */
export const uploadFileViaPresignedUrl = async (
	file: File,
	uploadPath: 'CREATOR' | 'USER' | 'EDU' = 'CREATOR',
): Promise<string> => {
	// file.type이 없는 경우 확장자로 MIME 타입 추론
	const mimeType = file.type || inferMimeType(file.name);

	let preSignedUrl: string;
	let fileUrl: string;
	try {
		const res = await getPresignedUploadUrl({
			fileType: mimeType,
			fileName: file.name,
			uploadPath,
		});
		preSignedUrl = res.preSignedUrl;
		fileUrl = res.fileUrl;
	} catch {
		return TEMP_UPLOAD_URL;
	}
	try {
		await uploadToS3(preSignedUrl, file, mimeType);
	} catch {
		return TEMP_UPLOAD_URL;
	}
	return fileUrl;
};

function inferMimeType(fileName: string): string {
	const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
	const map: Record<string, string> = {
		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		png: 'image/png',
		gif: 'image/gif',
		webp: 'image/webp',
		svg: 'image/svg+xml',
		mp4: 'video/mp4',
		mov: 'video/quicktime',
		pdf: 'application/pdf',
	};
	return map[ext] ?? 'application/octet-stream';
}
